import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { AppFooter } from '@/components/AppFooter';
import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Seo } from '@/seo/Seo';
import { pageSeo } from '@/seo/pageMeta';

/**
 * Rendered for any unmatched URL (the router's `*` route) and reused by
 * RouteErrorBoundary for thrown errors, so the app never falls back to
 * React Router's raw developer error screen.
 */
export function NotFoundPage({
  code = '404',
  title,
  message,
}: {
  code?: string;
  title?: string;
  message?: string;
}) {
  const { t } = useTranslation();
  const displayTitle = title ?? t('notFound.title');
  const displayMessage = message ?? t('notFound.message');
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Seo {...pageSeo.notFound} />

      <Box
        component="header"
        sx={{
          px: { xs: 2, md: 4 },
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <BrandLogo compact />
          <ThemeToggle />
        </Stack>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Paper
            variant="outlined"
            sx={(theme) => ({
              borderRadius: 3,
              p: { xs: 3, sm: 4.5 },
              textAlign: 'center',
              boxShadow: `0 24px 60px ${alpha(
                theme.palette.common.black,
                theme.palette.mode === 'light' ? 0.08 : 0.32
              )}`,
            })}
          >
            <Stack spacing={2.5} sx={{ alignItems: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  lineHeight: 1,
                  fontSize: { xs: '3.5rem', sm: '4.5rem' },
                  color: 'primary.main',
                }}
              >
                {code}
              </Typography>

              <Stack spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h1" sx={{ fontSize: { xs: '1.6rem', sm: '2rem' }, lineHeight: 1.15 }}>
                  {displayTitle}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
                  {displayMessage}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%', maxWidth: 420 }}>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                >
                  {t('notFound.backHome')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/app"
                  variant="contained"
                  size="large"
                  startIcon={<DashboardIcon />}
                  fullWidth
                >
                  {t('notFound.goDashboard')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <AppFooter />
    </Box>
  );
}
